import React, { Suspense, lazy } from 'react';
import { Route, Switch, Redirect } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, SubMenu } from 'components/navbar/navbar';
import { mediaCenterUrl, PATHS } from 'config';
import LandingPage from 'feature/common/landing';
import PageNotFound from 'feature/common/page-not-found';
import AppLayout from 'components/layout/app-layout';
import Navbar from 'components/navbar';
import { PoiContextProvider } from 'feature/poi-module/use-poi';
import { ProviderRouter } from 'utils/provider-helper';
import { deviceType, BROWSER } from 'utils/device-util';
import { currentUrl } from 'utils/url-helper';
import Footer from 'components/footer';
import AppServices from 'feature/distributor/autopass/app-services/app-services';
import { enterpriseUrl } from 'config';
import { EnterpriseLink } from 'app-router/mochipay/mochipay-router';

const RefuelingPage = lazy(() => import('feature/common/refueling'));
const ParkingPage = lazy(() => import('feature/common/parking'));
const OnStreetParkingPage = lazy(() => import('feature/common/on-street-parking'));
const AutoDetailingPage = lazy(() => import('feature/common/auto-detailing'));
const GasStationPage = lazy(() => import('feature/common/gas-station'));
const ParkinglotsPage = lazy(() => import('feature/common/parkinglots'));
const CarBeautyStoresPage = lazy(() => import('feature/common/car-beauty-stores'));
const AutoRepairStoresPage = lazy(() => import('feature/common/auto-repair-stores'));
const CarWashStoresPage = lazy(() => import('feature/common/car-wash-stores'));
const ChargingStoresPage = lazy(() => import('feature/common/charging-stores'));
const PromotionPage = lazy(() => import('feature/common/promotion'));
const PromotionDetailPage = lazy(() => import('feature/common/promotion/detail'));
const PromotionGroupPage = lazy(() => import('feature/common/promotion/group'));

const AutopassRouter = () => {
  const { t } = useTranslation();

  const navbarMenu = [
    new Menu(t('navbar.refueling'), PATHS.REFUELING),
    new Menu(t('navbar.parking'), PATHS.PARKING),
    new Menu(t('navbar.on_street_parking'), PATHS.ON_STREET_PARKING),
    new Menu(t('navbar.auto_detailing'), PATHS.AUTO_DETAILING),
    new SubMenu(t('navbar.location'), [
      new Menu(t('navbar.gas_station'), PATHS.GAS_STATIONS, true, 'gas-station'),
      new Menu(t('navbar.parking_lot'), PATHS.PARKINGLOTS, true, 'parkinglot'),
      // new Menu(t('navbar.car_beauty'), PATHS.CAR_BEAUTY_STORES, true, 'car-beauty-store'),
      new Menu(t('navbar.auto_repair'), PATHS.AUTO_REPAIR_STORES, true, 'auto-repair-store'),
      // new Menu(t('navbar.car_wash'), PATHS.CAR_WASH_STORES, true, 'car-wash-store'),
    ]),
    new Menu(t('navbar.promotion'), PATHS.PROMOTION),
    new Menu(t('navbar.help'), 'https://help.mochipay.xyz/', false),
    new Menu((<EnterpriseLink />), enterpriseUrl(), false),
  ];

  const navbarMobileMenu = [
    {
      title: t('navbar.about'),
      menus: [
        new Menu(t('navbar.refueling'), PATHS.REFUELING),
        new Menu(t('navbar.parking'), PATHS.PARKING),
        new Menu(t('navbar.on_street_parking'), PATHS.ON_STREET_PARKING),
        new Menu(t('navbar.auto_detailing'), PATHS.AUTO_DETAILING),
      ],
    },
    {
      title: t('navbar.location'),
      menus: [
        new Menu(t('navbar.gas_station'), PATHS.GAS_STATIONS, true, 'gas-station'),
        new Menu(t('navbar.auto_repair'), PATHS.AUTO_REPAIR_STORES, true, 'auto-repair-store'),
        new Menu(t('navbar.parking_lot'), PATHS.PARKINGLOTS, true, 'parkinglot'),
        // new Menu(t('navbar.car_wash'), PATHS.CAR_WASH_STORES, true, 'car-wash-store'),
        // new Menu(t('navbar.car_beauty'), PATHS.CAR_BEAUTY_STORES, true, 'car-beauty-store'),
      ],
    },
    {
      title: t('navbar.support'),
      menus: [
        new Menu(t('navbar.promotion'), PATHS.PROMOTION),
        new Menu(t('navbar.help'), 'https://help.mochipay.xyz/', false),
        new Menu((<EnterpriseLink />), enterpriseUrl(), false),
      ],
    },
  ];

  const footerMenu = [
    new SubMenu(t('footer.about'), [
      new Menu(t('footer.refueling'), PATHS.REFUELING),
      new Menu(t('footer.parking'), PATHS.PARKING),
      new Menu(t('footer.on_street_parking'), PATHS.ON_STREET_PARKING),
      new Menu(t('footer.auto_detailing'), PATHS.AUTO_DETAILING),
    ]),
    new SubMenu(t('footer.location'), [
      new Menu(t('footer.gas_station'), PATHS.GAS_STATIONS),
      new Menu(t('footer.parking_lot'), PATHS.PARKINGLOTS),
      // new Menu(t('footer.car_beauty'), PATHS.CAR_BEAUTY_STORES),
      new Menu(t('footer.auto_repair'), PATHS.AUTO_REPAIR_STORES),
      // new Menu(t('footer.car_wash'), PATHS.CAR_WASH_STORES),
    ]),
    new SubMenu(t('footer.support'), [
      new Menu(t('footer.promotion'), PATHS.PROMOTION),
      new Menu(t('footer.help'), 'https://help.mochipay.xyz/', false),
      new Menu((<EnterpriseLink icon={false} />), enterpriseUrl(), false),
    ]),
    new SubMenu(t('footer.newsroom'), [
      new Menu(t('footer.about_us'), `${mediaCenterUrl()}/company-info`, false),
      new Menu(t('footer.latest_news'), `${mediaCenterUrl()}/press-release`, false),
      new Menu(
        t('footer.media_coverages'),
        'https://docs.google.com/document/d/1TxMC54L3Dk3RDXgX-WHNMuP07N9RAEARiSsCaGf6FAA/edit?usp=sharing',
        false
      ),
      new Menu(
        t('footer.brand_resources'),
        'https://drive.google.com/drive/u/0/folders/1shEY6IQe5v62pT3eD2M-9UE6kgxp1LEc',
        false
      ),
    ]),
  ];

  const RouterContainer = ({ children }) => {
    return (
      <>
        {deviceType() === BROWSER && (
          <Navbar
            menus={navbarMenu}
            mobileMenus={navbarMobileMenu}
            distributorIcon={t('images:logo_autopass')}
            loginAvailable={false}
          />
        )}
        {children}
        {deviceType() === BROWSER && <Footer menus={footerMenu} />}
      </>
    );
  };

  return (
    <AppLayout>
      <Suspense fallback={''}>
        <Switch>
          {/* 重新導向舊有 auto-repair 連結 */}
          <Route exact path="/auto-repair">
            <Redirect to={PATHS.AUTO_DETAILING} />
          </Route>
          <Route
            exact
            path={currentUrl('/')}
            component={() => (
              <RouterContainer>
                <LandingPage />
              </RouterContainer>
            )}
          />
          <Route
            path={currentUrl(PATHS.REFUELING)}
            component={() => (
              <RouterContainer>
                <RefuelingPage />
              </RouterContainer>
            )}
          />
          <Route
            path={currentUrl(PATHS.PARKING)}
            component={() => (
              <RouterContainer>
                <ParkingPage />
              </RouterContainer>
            )}
          />
          <Route
            path={currentUrl(PATHS.ON_STREET_PARKING)}
            component={() => (
              <RouterContainer>
                <OnStreetParkingPage />
              </RouterContainer>
            )}
          />
          <Route
            path={currentUrl(PATHS.AUTO_DETAILING)}
            component={() => (
              <RouterContainer>
                <AutoDetailingPage />
              </RouterContainer>
            )}
          />
          <ProviderRouter
            provider={PoiContextProvider}
            component={() => (
              <RouterContainer>
                <GasStationPage />
              </RouterContainer>
            )}
            path={currentUrl(PATHS.GAS_STATIONS)}
          />
          <ProviderRouter
            provider={PoiContextProvider}
            component={() => (
              <RouterContainer>
                <ParkinglotsPage />
              </RouterContainer>
            )}
            path={currentUrl(PATHS.PARKINGLOTS)}
          />
          <ProviderRouter
            provider={PoiContextProvider}
            component={() => (
              <RouterContainer>
                <CarBeautyStoresPage />
              </RouterContainer>
            )}
            path={currentUrl(PATHS.CAR_BEAUTY_STORES)}
          />

          <ProviderRouter
            provider={PoiContextProvider}
            component={() => (
              <RouterContainer>
                <AutoRepairStoresPage />
              </RouterContainer>
            )}
            path={currentUrl(PATHS.AUTO_REPAIR_STORES)}
          />
          <ProviderRouter
            provider={PoiContextProvider}
            component={() => (
              <RouterContainer>
                <CarWashStoresPage />
              </RouterContainer>
            )}
            path={currentUrl(PATHS.CAR_WASH_STORES)}
          />
          <ProviderRouter
            provider={PoiContextProvider}
            component={() => (
              <RouterContainer>
                <ChargingStoresPage />
              </RouterContainer>
            )}
            path={currentUrl(PATHS.CHARGING_STORES)}
          />
          <Route
            path={currentUrl(PATHS.PROMOTION)}
            component={() => (
              <RouterContainer>
                <PromotionPage />
              </RouterContainer>
            )}
          />
          <Route
            path={currentUrl(PATHS.PROMOTION_DETAIL)}
            component={() => (
              <RouterContainer>
                <PromotionDetailPage />
              </RouterContainer>
            )}
          />
          <Route
            path={currentUrl(PATHS.PROMOTION_GROUP)}
            component={() => (
              <RouterContainer>
                <PromotionGroupPage />
              </RouterContainer>
            )}
          />
          <Route psth={PATHS.APP_SERVICES} component={AppServices} />
          <Route path="*" component={PageNotFound} />
        </Switch>
      </Suspense>
    </AppLayout>
  );
};

export default AutopassRouter;
